import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import ImageExt from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Bold, Italic, Underline as UnderIcon, List, ListOrdered,
    AlignRight, AlignCenter, AlignLeft, AlignJustify,
    Link as LinkIcon, Image as ImageIcon, Palette, Highlighter, Undo, Redo, Table as TableIcon, Heading1, Heading2, Heading3, Quote, Minus,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const FONTS = [
    { v: "", l: "الافتراضي" },
    { v: "'IBM Plex Sans Arabic', sans-serif", l: "IBM Plex Sans Arabic" },
    { v: "'Cairo', sans-serif", l: "Cairo" },
    { v: "'Tajawal', sans-serif", l: "Tajawal" },
    { v: "'Amiri', serif", l: "Amiri (تراثي)" },
    { v: "Georgia, serif", l: "Georgia" },
    { v: "Arial, sans-serif", l: "Arial" },
];

function Btn({ active, onClick, title, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            data-testid={testid}
            className={`w-8 h-8 rounded-md flex items-center justify-center text-sm transition-colors ${active ? "bg-[#1B2A4E] text-white" : "text-slate-600 hover:bg-slate-200"}`}
        >
            {children}
        </button>
    );
}

export default function TipTapEditor({ value, onChange }) {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({ types: ["heading", "paragraph"], defaultAlignment: "right" }),
            ImageExt.configure({ inline: false, HTMLAttributes: { class: "rounded-lg" } }),
            LinkExt.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[#1B2A4E] underline" } }),
            Table.configure({ resizable: true }),
            TableRow, TableHeader, TableCell,
        ],
        content: value || "",
        onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
        editorProps: {
            attributes: {
                class: "tiptap-content prose max-w-none focus:outline-none min-h-[280px] p-4",
                dir: "rtl",
            },
        },
    });

    if (!editor) return <div className="border rounded-xl h-72 animate-pulse bg-slate-50" />;

    const setLink = () => {
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("رابط:", prev);
        if (url === null) return;
        if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const insertImageFromFile = async () => {
        const inp = document.createElement("input");
        inp.type = "file";
        inp.accept = "image/*";
        inp.onchange = async () => {
            const f = inp.files?.[0];
            if (!f) return;
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data } = await api.post("/uploads", fd);
                const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                editor.chain().focus().setImage({ src: url, alt: data.filename }).run();
            } catch (e) { toast.error(formatApiError(e)); }
        };
        inp.click();
    };

    const insertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

    return (
        <div dir="rtl" className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <div className="flex flex-wrap gap-1 border-b border-slate-100 p-2 bg-slate-50">
                <Btn onClick={() => editor.chain().focus().undo().run()} title="تراجع" testid="editor-undo"><Undo className="w-4 h-4" /></Btn>
                <Btn onClick={() => editor.chain().focus().redo().run()} title="إعادة" testid="editor-redo"><Redo className="w-4 h-4" /></Btn>
                <div className="w-px bg-slate-200 mx-1" />

                <Btn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="عنوان 1" testid="editor-h1"><Heading1 className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="عنوان 2" testid="editor-h2"><Heading2 className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="عنوان 3" testid="editor-h3"><Heading3 className="w-4 h-4" /></Btn>

                <div className="w-px bg-slate-200 mx-1" />
                <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="عريض" testid="editor-bold"><Bold className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="مائل" testid="editor-italic"><Italic className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="تحته خط" testid="editor-underline"><UnderIcon className="w-4 h-4" /></Btn>

                <label className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-200 cursor-pointer relative" title="لون النص">
                    <Palette className="w-4 h-4" />
                    <input type="color" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="editor-color" />
                </label>
                <label className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-200 cursor-pointer relative" title="لون الخلفية">
                    <Highlighter className="w-4 h-4" />
                    <input type="color" onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="editor-highlight" />
                </label>

                <div className="w-px bg-slate-200 mx-1" />
                <Btn active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="محاذاة يمين" testid="editor-align-right"><AlignRight className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="محاذاة وسط" testid="editor-align-center"><AlignCenter className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="محاذاة يسار" testid="editor-align-left"><AlignLeft className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()} title="ضبط" testid="editor-align-justify"><AlignJustify className="w-4 h-4" /></Btn>

                <div className="w-px bg-slate-200 mx-1" />
                <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="قائمة نقطية" testid="editor-ul"><List className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="قائمة رقمية" testid="editor-ol"><ListOrdered className="w-4 h-4" /></Btn>
                <Btn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="اقتباس"><Quote className="w-4 h-4" /></Btn>
                <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="فاصل"><Minus className="w-4 h-4" /></Btn>

                <div className="w-px bg-slate-200 mx-1" />
                <Btn active={editor.isActive("link")} onClick={setLink} title="رابط" testid="editor-link"><LinkIcon className="w-4 h-4" /></Btn>
                <Btn onClick={insertImageFromFile} title="صورة" testid="editor-image"><ImageIcon className="w-4 h-4" /></Btn>
                <Btn onClick={insertTable} title="جدول" testid="editor-table"><TableIcon className="w-4 h-4" /></Btn>
            </div>
            <EditorContent editor={editor} />
        </div>
    );
}
